import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import type { AnalyticsTrackFn } from './types.js';

export type StudioBootstrapPickerMode = 'bridge_setup' | 'studio_access' | 'undetermined';
export type StudioBootstrapBridgeState = 'resolving' | 'linked' | 'unlinked' | 'error';
export type StudioBootstrapTargetStatus = 'available' | 'linked' | 'create' | 'none';
export type StudioBootstrapAction =
  | 'resolve'
  | 'open'
  | 'link'
  | 'add'
  | 'replace'
  | 'create'
  | 'fetch';
export type StudioBootstrapOutcome =
  | 'started'
  | 'success'
  | 'failure'
  | 'denied'
  | 'conflict'
  | 'cancelled';
export type StudioBootstrapErrorType =
  | 'authentication'
  | 'permission_denied'
  | 'inventory_conflict'
  // ENG-4483 W2/W3: service-named refusals, distinct from a generic conflict.
  | 'capacity_exhausted'
  | 'capacity_unknown'
  | 'capability_unavailable'
  | 'network'
  | 'invalid_response'
  | 'bridge_handshake'
  | 'unknown';

/**
 * Deliberately bounded properties for the account bootstrap funnel. Studio and
 * Desktop Bridge IDs are opaque registry identifiers that tie a row to its
 * Studio and Bridges; names, emails, and organization data stay out.
 */
export interface StudioBootstrapProperties {
  readonly surface: 'electron_account_bootstrap';
  readonly picker_mode: StudioBootstrapPickerMode;
  readonly local_bridge_state: StudioBootstrapBridgeState;
  readonly studio_count: number;
  readonly target_link_status?: StudioBootstrapTargetStatus;
  readonly action?: StudioBootstrapAction;
  readonly outcome?: StudioBootstrapOutcome;
  readonly error_type?: StudioBootstrapErrorType;
  /** The Studio the event refers to. */
  readonly studio_id?: string;
  /** This computer's Desktop Bridge. */
  readonly agent_id?: string;
  /** Desktop Bridges already linked to the Studio: displaced by a replace, kept by an add. */
  readonly existing_agent_ids?: readonly string[];
}

// Per rule: private-diagnostics-use-positive-allowlists — only these keys reach PostHog.
const STUDIO_BOOTSTRAP_PROPERTY_KEYS = [
  'surface',
  'picker_mode',
  'local_bridge_state',
  'studio_count',
  'target_link_status',
  'action',
  'outcome',
  'error_type',
  'studio_id',
  'agent_id',
  'existing_agent_ids'
] as const satisfies readonly (keyof StudioBootstrapProperties)[];

type StudioBootstrapEventName =
  | typeof EVENT_NAMES.BRIDGE_BOOTSTRAP_RESOLVED
  | typeof EVENT_NAMES.ROOMS_FETCH_FAILED
  | typeof EVENT_NAMES.STUDIO_CREATED_SILENT
  | typeof EVENT_NAMES.STUDIO_PICKER_OPENED
  | typeof EVENT_NAMES.STUDIO_SELECTED
  | typeof EVENT_NAMES.STUDIO_PICKER_RENDER_FAILED
  | typeof EVENT_NAMES.STUDIO_LINK_FAILED
  | typeof EVENT_NAMES.STUDIO_CREATE_REQUESTED
  | typeof EVENT_NAMES.STUDIO_CREATED_FROM_PICKER
  | typeof EVENT_NAMES.STUDIO_CREATE_FAILED
  | typeof EVENT_NAMES.STUDIO_LINK_PERMISSION_DENIED
  | typeof EVENT_NAMES.STUDIO_LINK_SUCCEEDED
  | typeof EVENT_NAMES.STUDIO_SETUP_SUCCEEDED
  | typeof EVENT_NAMES.STUDIO_MULTIBRIDGE_CONFIRMATION_OPENED
  | typeof EVENT_NAMES.STUDIO_MULTIBRIDGE_CONFIRMATION_CONFIRMED
  | typeof EVENT_NAMES.STUDIO_MULTIBRIDGE_CONFIRMATION_CANCELLED
  | typeof EVENT_NAMES.STUDIO_REPLACEMENT_CONFIRMATION_OPENED
  | typeof EVENT_NAMES.STUDIO_REPLACEMENT_CONFIRMATION_CONFIRMED
  | typeof EVENT_NAMES.STUDIO_REPLACEMENT_CONFIRMATION_CANCELLED;

/** One typed capture boundary prevents accidental names, emails, or other fields entering PostHog. */
export const createStudioBootstrapTracker = (track: AnalyticsTrackFn) => ({
  track(event: StudioBootstrapEventName, properties: StudioBootstrapProperties): void {
    // Copy only allowlisted keys: a caller's spread cannot widen the payload at runtime.
    const payload: Record<string, unknown> = {};
    for (const key of STUDIO_BOOTSTRAP_PROPERTY_KEYS) {
      const value = properties[key];
      if (value !== undefined) payload[key] = Array.isArray(value) ? [...value] : value;
    }
    track(event, EVENT_CATEGORIES.BRIDGE, payload);
  }
});
